import { useEffect, useState } from 'react'
import { enablePush, pushPermission, pushSupported } from '../../services/pushService'

/**
 * Turning notifications on.
 *
 * A button rather than something that happens on load, because the permission prompt can
 * only ever be shown **once**: a prompt that appears unprompted is the one people dismiss
 * without reading, and after that they cannot be asked again from code — only through
 * browser settings, which nobody finds.
 *
 * It renders nothing at all where it cannot work. An iPhone on Safari that has not
 * installed the app to the Home Screen genuinely cannot receive web push, and a switch
 * that does nothing is worse than no switch.
 */
export default function NotifyToggle({ label = 'Get notified about this order' }) {
  const [supported, setSupported] = useState(null)
  const [permission, setPermission] = useState(pushPermission())
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState(null)

  useEffect(() => {
    pushSupported().then(setSupported)
  }, [])

  // Still deciding, or this browser cannot do it. Either way, say nothing.
  if (supported !== true) return null

  if (permission === 'granted') {
    return (
      <p className="mt-3 text-body-s text-ink-3">
        Notifications are on for this browser.
      </p>
    )
  }

  /**
   * Denied is a dead end, and saying so is the honest thing.
   *
   * `requestPermission()` resolves instantly with `denied` and shows nothing, so a button
   * here would look broken. Only the browser's own settings can undo it.
   */
  if (permission === 'denied') {
    return (
      <p className="mt-3 text-body-s text-ink-3">
        Notifications are blocked for this site. Your browser&apos;s site settings are the only
        place that can turn them back on.
      </p>
    )
  }

  const turnOn = async () => {
    setBusy(true)
    setProblem(null)

    const result = await enablePush()

    setPermission(pushPermission())
    setBusy(false)

    if (result.ok) return

    setProblem(
      result.reason === 'not_configured'
        ? 'Notifications are not set up on this site yet.'
        : result.reason === 'unsupported'
          ? 'This browser cannot show notifications.'
          : result.reason === 'denied'
            ? 'You said no — your browser settings can change that.'
            : 'That did not work. Try again in a moment.',
    )
  }

  return (
    <div className="mt-3">
      <button
        type="button"
        onClick={turnOn}
        disabled={busy}
        className="inline-flex items-center min-h-touch px-3.5 rounded-chip border border-edge
                   bg-surface text-body-s font-semibold text-brand
                   transition-colors duration-quick ease-out hover:border-brand disabled:opacity-60"
      >
        {busy ? 'Asking…' : label}
      </button>

      {problem && <p className="mt-1.5 text-body-s text-alert">{problem}</p>}
    </div>
  )
}
